/**
 * A calendar date split into its numeric parts. `month` is 1-12 and
 * `day` is 1-31 — human values, not the 0-based month a JS `Date` uses.
 */
export interface DateParts {
  year: number
  month: number
  day: number
}

/** Options for {@link parseDateInput}. */
export interface ParseDateInputOptions {
  /**
   * Locale whose date format decides field order — `'de-DE'` and
   * `'en-GB'` type the day before the month, `'en-US'` types the month
   * first. The order is read at runtime from `Intl.DateTimeFormat`, so
   * any locale works, not only the formats the app currently ships.
   */
  locale: string
  /**
   * Date used to fill in the parts the user left out: a lone number
   * borrows this month and year, a day-and-month pair borrows this
   * year. Pass today's date for the usual behaviour.
   */
  reference: DateParts
  /**
   * When `true`, a lone day that has already passed in the reference
   * month rolls forward to the next month — e.g. with a reference of
   * 18 May, typing `'12'` yields 12 June, not 12 May. Multi-part input
   * (a day with an explicit month) is never rolled. Off by default.
   */
  futureDate?: boolean
}

type DateField = 'day' | 'month' | 'year'

/** Any run of these characters separates one date field from the next. */
const SEPARATORS = /[.,/\s-]+/

/**
 * A fixed, unambiguous date (2 Jan 2000) used purely to read a locale's
 * field order back out of `Intl` — day and month differ, so the order
 * is never ambiguous.
 */
const SAMPLE_DATE = new Date(2000, 0, 2)

function isLeapYear(year: number): boolean {
  return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return isLeapYear(year) ? 29 : 28
  return month === 4 || month === 6 || month === 9 || month === 11 ? 30 : 31
}

function getOrderedDateParts(locale: string): Intl.DateTimeFormatPart[] {
  return new Intl.DateTimeFormat(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  }).formatToParts(SAMPLE_DATE)
}

/**
 * Resolve whether a locale types day, month and year first, second or
 * third. Falls back to day-month-year (the app default) when the locale
 * string is invalid.
 */
function resolveFieldOrder(locale: string): DateField[] {
  try {
    const order = getOrderedDateParts(locale)
      .map((part) => part.type)
      .filter(
        (type): type is DateField =>
          type === 'day' || type === 'month' || type === 'year'
      )
    if (order.length === 3) return order
  } catch {
    // Invalid locale — fall through to the default order.
  }
  return ['day', 'month', 'year']
}

/**
 * Expand a typed year token into a full year. One- and two-digit tokens
 * are read as 2000s (`'7'` → `2007`, `'26'` → `2026`); three- and
 * four-digit tokens are taken literally. Anything longer is rejected.
 */
function expandYear(token: string): number | undefined {
  if (token.length <= 2) return 2000 + Number(token)
  if (token.length <= 4) return Number(token)
  return undefined
}

/**
 * Expand a partially-typed date into a full `{ year, month, day }`.
 *
 * The input is split on any run of separators (`.` `/` `-` `,` or
 * whitespace); what each part means depends on `options.locale`:
 *
 * - **One part** is always the day — `'12'` is the 12th of the
 *   reference month, even in month-first locales (or of next month,
 *   when `options.futureDate` rolls a day that has already passed).
 * - **Two parts** are the day and month in the locale's order, so
 *   `'12.1'` is 12 Jan in `de-DE` but 1 Dec in `en-US`.
 * - **Three parts** are the day, month and year in the locale's order.
 *
 * Parts the user left out come from `options.reference`. Returns
 * `undefined` for anything that is not a real calendar date — including
 * a run of digits with no separator (`'1205'`) — so the caller can
 * revert the field.
 */
/**
 * A lone number is always the day; month and year come from the
 * reference date. `futureDate`: a day that has already passed in the
 * reference month is read as the same day of the next month instead.
 */
function parseLoneDay(day: number, options: ParseDateInputOptions): DateParts {
  const { reference } = options
  let month = reference.month
  let year = reference.year
  if (options.futureDate && day < reference.day) {
    month += 1
    if (month > 12) {
      month = 1
      year += 1
    }
  }
  return { year, month, day }
}

/**
 * Two or three parts follow the locale's field order. With two parts
 * the year is dropped, leaving just day and month; a missing year comes
 * from the reference date.
 */
function parseOrderedTokens(
  tokens: string[],
  options: ParseDateInputOptions
): DateParts | undefined {
  const order = resolveFieldOrder(options.locale)
  const fields =
    tokens.length === 2 ? order.filter((field) => field !== 'year') : order

  let dayToken: string | undefined
  let monthToken: string | undefined
  let yearToken: string | undefined
  fields.forEach((field, index) => {
    const token = tokens[index]
    if (field === 'day') dayToken = token
    else if (field === 'month') monthToken = token
    else yearToken = token
  })

  if (dayToken === undefined || monthToken === undefined) return undefined
  const day = Number(dayToken)
  const month = Number(monthToken)

  if (yearToken === undefined) {
    return { year: options.reference.year, month, day }
  }
  const expanded = expandYear(yearToken)
  if (expanded === undefined) return undefined
  return { year: expanded, month, day }
}

export function parseDateInput(
  input: string,
  options: ParseDateInputOptions
): DateParts | undefined {
  const tokens = input.trim().split(SEPARATORS).filter(Boolean)
  if (tokens.length === 0 || tokens.length > 3) return undefined
  if (tokens.some((token) => !/^\d+$/.test(token))) return undefined

  const parts =
    tokens.length === 1
      ? parseLoneDay(Number(tokens[0]), options)
      : parseOrderedTokens(tokens, options)
  if (!parts) return undefined

  const { year, month, day } = parts
  if (month < 1 || month > 12) return undefined
  if (year < 1900 || year > 2100) return undefined
  if (day < 1 || day > daysInMonth(year, month)) return undefined

  return parts
}

/** Options for {@link maskDateInput}. */
export interface MaskDateInputOptions {
  /**
   * Emit the separator that follows a *completed* field as soon as its last
   * digit is typed — `'01'` → `'01.'` — instead of waiting for the next digit.
   * Off by default. Callers using eager mode must suppress re-masking on
   * deletion, or the auto-added separator can't be backspaced past.
   */
  eager?: boolean
}

/**
 * Insert a locale's date separators as the user types, so a digits-only
 * keypad still produces `23.04.2027`. Digits from `input` are laid into
 * `pattern`'s field slots (any letter is a digit slot, anything else is a
 * separator). A separator normally appears only once a digit follows it — so
 * backspacing never leaves a dangling one — unless the user just typed a
 * separator, or `options.eager` is set (then a completed field's separator is
 * added immediately). Digits past the pattern are dropped.
 *
 * `pattern` is a format string as {@link getDateFormatPattern} returns
 * (`DD.MM.YYYY`, `MM/DD/YYYY`, `YYYY-MM-DD`). Keyboard-agnostic: on mobile a
 * numeric keypad needs no separator key, and on desktop typed separators are
 * folded in. The result is still parsed by {@link parseDateInput} on commit.
 */
export function maskDateInput(
  input: string,
  pattern: string,
  options: MaskDateInputOptions = {}
): string {
  const digits = input.replace(/\D/g, '')
  if (!digits) return ''

  let out = ''
  let consumed = 0
  for (const char of pattern) {
    if (consumed >= digits.length) break
    if (/[a-z]/i.test(char)) {
      out += digits[consumed]
      consumed += 1
    } else {
      out += char
    }
  }

  // Append the separator that follows a just-completed field when the caller
  // opts into eager separators, or the user just typed one (so manual entry
  // stays responsive) — but only when a separator is actually next.
  const nextChar = pattern[out.length]
  const separatorNext = nextChar !== undefined && !/[a-z]/i.test(nextChar)
  const allDigitsPlaced = consumed >= digits.length
  if (
    allDigitsPlaced &&
    separatorNext &&
    (options.eager || /\D$/.test(input))
  ) {
    out += nextChar
  }

  return out
}

/**
 * Build a human-readable format hint for a locale — `'DD.MM.YYYY'` for
 * `de-DE`, `'MM/DD/YYYY'` for `en-US`. Useful as an input placeholder
 * so the user can see which field is expected first.
 */
export function getDateFormatPattern(locale: string): string {
  try {
    return getOrderedDateParts(locale)
      .map((part) => {
        if (part.type === 'day') return 'DD'
        if (part.type === 'month') return 'MM'
        if (part.type === 'year') return 'YYYY'
        return part.value
      })
      .join('')
  } catch {
    // Invalid locale — fall back to the app default format.
  }
  return 'DD.MM.YYYY'
}
